import React from 'react';
import axios from 'axios';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  BiSolidUser,
  BiPhone,
  BiReceipt,
  BiTimeFive,
  BiLoaderCircle,
  BiSolidCheckCircle,
  BiErrorCircle,
  BiMinusCircle,
} from 'react-icons/bi';
import { toast } from 'react-toastify';
import { Locations } from '@gql/base-types';
import { useUpdateAppointmentMutation } from '../queries/updateAppointment.mutation.generated';
import { useGetAllLocationsQuery } from '../../locations/queries/getAllLocations.query.generated';
import { createAppointment, APPOINTMENT_TYPE, APPOINTMENT_SOURCE } from '@hopdrive/sdk-appointments';
import { validatePhone, validateVin } from '@services/validationService';
import { useRooftop, useUser } from '@store';
import { useReloadGuard } from '@hopdrive/sdk-app-updater/react';
import {
  MovePlannerLocationSelect,
  VinLookup,
  MakeSelect,
  ModelSelect,
  YearSelect,
  ColorSelect,
} from '@hopdrive/feature-move-planner';

import { css, keyframes } from '@emotion/css';

/**
 * Structural shape of the appointment row the edit mode consumes. Typed
 * structurally (not as the generated `Appointments` type) so callers can pass
 * query-row subsets like `GetMobileServiceAppointmentsQuery['appointments'][number]`.
 */
export interface EditableAppointment {
  id: number;
  consumer_name?: string | null;
  consumer_phone?: string | null;
  appointment_time?: string | null;
  ro_number?: string | null;
  config?: unknown;
  vehicle_vin?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_color?: string | null;
  driver_notes?: string | null;
  pickup?: { id: number; name?: string | null; address?: string | null; latitude?: number | null; longitude?: number | null } | null;
}

interface AddAppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** When set, the dialog edits this appointment in place instead of creating a new one. */
  appointment?: EditableAppointment | null;
}

/** Bounds for the coordinator-entered per-visit service time. */
const SERVICE_MINUTES_MIN = 5;
const SERVICE_MINUTES_MAX = 480;
const SERVICE_MINUTES_DEFAULT = 30;

/**
 * The appointment config keys this dialog reads/writes. The column is jsonb,
 * so this is the one place the untyped payload gets a shape; other keys are
 * preserved by merging (see handleSubmit).
 */
type AppointmentConfig = { estimated_service_minutes?: number };

const configOf = (appt: EditableAppointment): AppointmentConfig =>
  (appt.config as AppointmentConfig | null | undefined) ?? {};

const INITIAL_STATE = {
  consumerName: '',
  consumerPhone: '',
  appointmentTime: '',
  roNumber: '',
  serviceMinutes: String(SERVICE_MINUTES_DEFAULT),
  vehicleVin: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleYear: '',
  vehicleColor: '',
  driverNotes: '',
};

/** Formats a phone number progressively as the user types */
const formatPhoneProgressive = (value: string): string => {
  const raw = value.replace(/\D/g, '');
  const digits = raw.length > 10 && raw.startsWith('1') ? raw.slice(1) : raw;
  const len = digits.length;
  if (len === 0) return '';
  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const formFromAppointment = (appt: EditableAppointment): typeof INITIAL_STATE => ({
  consumerName: appt.consumer_name || '',
  consumerPhone: formatPhoneProgressive(appt.consumer_phone || ''),
  appointmentTime: appt.appointment_time ? dayjs(appt.appointment_time).format('YYYY-MM-DDTHH:mm') : '',
  roNumber: appt.ro_number || '',
  serviceMinutes: String(configOf(appt).estimated_service_minutes ?? SERVICE_MINUTES_DEFAULT),
  vehicleVin: appt.vehicle_vin || '',
  vehicleMake: appt.vehicle_make || '',
  vehicleModel: appt.vehicle_model || '',
  vehicleYear: appt.vehicle_year ? String(appt.vehicle_year) : '',
  vehicleColor: appt.vehicle_color || '',
  driverNotes: appt.driver_notes || '',
});

const AddAppointmentDialog: React.FC<AddAppointmentDialogProps> = ({ open, onClose, onSuccess, appointment }) => {
  const theme = useTheme();
  const rooftop = useRooftop();
  const user = useUser();
  const isEdit = Boolean(appointment);
  const [form, setForm] = React.useState(INITIAL_STATE);
  const [location, setLocation] = React.useState<Locations | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  /** SMS-capability verdict from verifyMobilePhone: true/false once checked, null when unknown. */
  const [isMobilePhone, setIsMobilePhone] = React.useState<boolean | null>(null);
  const [phoneVerifying, setPhoneVerifying] = React.useState(false);
  const [minutesError, setMinutesError] = React.useState<string | null>(null);
  const [vinError, setVinError] = React.useState<string | null>(null);
  const [updateAppointment] = useUpdateAppointmentMutation();

  const customerId = rooftop?.id || user?.profile?.customer_id || undefined;

  // Saved/favorite locations for this rooftop — fed to the address picker so
  // the coordinator can pick a known customer location (favorites sort first).
  const { data: locationsData, refetch: refetchLocations } = useGetAllLocationsQuery({
    variables: { customerId: customerId ?? 0 },
    skip: !customerId,
    fetchPolicy: 'cache-and-network',
  });
  const savedLocations = (locationsData?.locations ?? []) as (Locations | null)[];

  // What the form held when it opened. Changes from this are unsaved work.
  const [seed, setSeed] = React.useState<{ form: typeof INITIAL_STATE; locationId: number | null } | null>(null);

  // Prefill from the appointment each time the dialog opens in edit mode;
  // reset to a blank form when it opens for creation.
  React.useEffect(() => {
    if (!open) {
      setSeed(null);
      return;
    }
    if (appointment) {
      const seededForm = formFromAppointment(appointment);
      setForm(seededForm);
      setLocation((appointment.pickup as unknown as Locations) || null);
      setSeed({ form: seededForm, locationId: appointment.pickup?.id ?? null });
    } else {
      setForm(INITIAL_STATE);
      setLocation(null);
      setSeed({ form: INITIAL_STATE, locationId: null });
    }
    setPhoneError(null);
    setIsMobilePhone(null);
    setMinutesError(null);
    setVinError(null);
  }, [open, appointment]);

  const changedFromSeed =
    seed !== null &&
    ((Object.keys(seed.form) as (keyof typeof INITIAL_STATE)[]).some(key => form[key] !== seed.form[key]) ||
      (location?.id ?? null) !== seed.locationId);
  // Holds while the open form differs from what it opened with, or a save runs.
  const holdsUnsavedWork = (open && changedFromSeed) || loading;
  useReloadGuard(holdsUnsavedWork, 'Appointment');

  const handleChange = (field: keyof typeof INITIAL_STATE) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = formatPhoneProgressive(e.target.value);
    setForm(prev => ({ ...prev, consumerPhone: masked }));
    if (phoneError) setPhoneError(null);
    setIsMobilePhone(null); // stale verdict — the number changed
  };

  // Phone is optional, but when present it must be a valid US number.
  const checkPhone = (value: string): boolean => {
    if (!value.trim()) {
      setPhoneError(null);
      return true;
    }
    const valid = validatePhone(value);
    setPhoneError(valid ? null : 'Enter a valid 10-digit phone number');
    return valid;
  };

  // On blur: regex first, then the shared verifyMobilePhone endpoint (same
  // carrier/SMS-capability lookup the move planner uses). Advisory only —
  // the verdict renders as an adornment icon and never blocks submit, since
  // a landline is still a valid contact number for an appointment.
  const handlePhoneBlur = async () => {
    const value = form.consumerPhone;
    if (!checkPhone(value) || !value.trim()) {
      setIsMobilePhone(null);
      return;
    }
    setPhoneVerifying(true);
    try {
      const userToken = await user?.getToken();
      const res = await axios({
        method: 'POST',
        url: '/.netlify/functions/verifyMobilePhone',
        data: { phone: value },
        headers: { authorization: `Bearer ${userToken}` },
      });
      if (res?.data?.success && (res?.data?.isMobile === true || res?.data?.isMobile === false)) {
        setIsMobilePhone(res.data.isMobile);
      } else {
        setIsMobilePhone(null);
      }
    } catch (err) {
      console.error('Failed to verify mobile phone:', err);
      setIsMobilePhone(null);
    } finally {
      setPhoneVerifying(false);
    }
  };

  const checkMinutes = (value: string): boolean => {
    const minutes = Number(value);
    const valid =
      Number.isInteger(minutes) && minutes >= SERVICE_MINUTES_MIN && minutes <= SERVICE_MINUTES_MAX;
    setMinutesError(
      valid ? null : `Enter ${SERVICE_MINUTES_MIN}-${SERVICE_MINUTES_MAX} minutes`
    );
    return valid;
  };

  // `||` (not `??`) is deliberate: a lookup that omits a field — or returns
  // an empty/zero value for it — keeps whatever the user already entered,
  // rather than blanking it. Trade-off: a manually-cleared field also
  // survives a re-scan; acceptable, since the lookup result fills it anyway
  // whenever the VIN decodes.
  const handleVinChange = (vehicle?: any) => {
    setForm(prev => ({
      ...prev,
      vehicleVin: vehicle?.vehicle_vin || prev.vehicleVin,
      vehicleMake: vehicle?.vehicle_make || prev.vehicleMake,
      vehicleModel: vehicle?.vehicle_model || prev.vehicleModel,
      vehicleYear: vehicle?.vehicle_year || prev.vehicleYear,
      vehicleColor: vehicle?.vehicle_color || prev.vehicleColor,
    }));
  };

  const handleVinInputChange = (input?: string | null) => {
    setForm(prev => ({ ...prev, vehicleVin: input || '' }));
    if (vinError) setVinError(null);
  };

  // VIN is optional, but when present it must pass the check digit — in BOTH
  // create and edit. Create used to lean on the SDK's server-side validation
  // (surfacing as an opaque toast); edit wrote straight to Hasura and accepted
  // anything. Same optional-but-valid shape as checkPhone above.
  const checkVin = (value: string): boolean => {
    if (!value.trim()) {
      setVinError(null);
      return true;
    }
    const valid = validateVin(value);
    setVinError(valid ? null : 'Enter a valid 17-character VIN');
    return valid;
  };

  const handleSubmit = async () => {
    if (!form.consumerName.trim()) {
      toast.warning('Consumer name is required');
      return;
    }
    if (!location?.id) {
      toast.warning('Service address is required');
      return;
    }
    if (!checkPhone(form.consumerPhone)) {
      return;
    }
    if (!checkMinutes(form.serviceMinutes)) {
      return;
    }
    if (!checkVin(form.vehicleVin)) {
      toast.warning('Enter a valid VIN or clear the field');
      return;
    }

    if (!customerId) {
      toast.error('No customer context available');
      return;
    }

    const phoneDigits = form.consumerPhone.replace(/\D/g, '');
    const serviceMinutes = Number(form.serviceMinutes);
    // datetime-local gives an offset-less string ("2026-07-21T10:00"); dayjs
    // applies local-time semantics consistently, where new Date() parsing
    // varies by environment (some treat it as UTC).
    const appointmentTimeIso = form.appointmentTime ? dayjs(form.appointmentTime).toISOString() : null;

    setLoading(true);
    try {
      if (appointment) {
        await updateAppointment({
          variables: {
            id: appointment.id,
            changes: {
              consumer_name: form.consumerName,
              consumer_phone: phoneDigits || null,
              appointment_time: appointmentTimeIso,
              pickup_location_id: location.id,
              ro_number: form.roNumber || null,
              vehicle_vin: form.vehicleVin || null,
              vehicle_make: form.vehicleMake || null,
              vehicle_model: form.vehicleModel || null,
              vehicle_year: form.vehicleYear ? parseInt(form.vehicleYear, 10) : null,
              vehicle_color: form.vehicleColor || null,
              driver_notes: form.driverNotes || null,
              // Merge so sibling config keys written by other flows survive the edit.
              config: { ...configOf(appointment), estimated_service_minutes: serviceMinutes },
              updatedat: new Date().toISOString(),
              updatedby: user?.profile?.email || 'unknown',
            },
          },
        });
        toast.success('Appointment updated');
      } else {
        await createAppointment({
          customerId,
          type: APPOINTMENT_TYPE.MOBILE_SERVICE,
          source: APPOINTMENT_SOURCE.DEALER_PORTAL,
          consumerName: form.consumerName,
          consumerPhone: phoneDigits || null,
          appointmentTime: appointmentTimeIso,
          pickupLocationId: location.id,
          consumerAtPickup: true,
          vehicle: {
            vin: form.vehicleVin || null,
            make: form.vehicleMake || null,
            model: form.vehicleModel || null,
            year: form.vehicleYear ? parseInt(form.vehicleYear, 10) : null,
            color: form.vehicleColor || null,
          },
          roNumber: form.roNumber || null,
          config: { estimated_service_minutes: serviceMinutes },
          driverNotes: form.driverNotes || null,
          createdBy: user?.profile?.email || 'unknown',
        });
        toast.success('Appointment created');
      }

      setForm(INITIAL_STATE);
      setLocation(null);
      onSuccess();
    } catch (err: any) {
      toast.error(
        `Failed to ${appointment ? 'update' : 'create'} appointment: ${err?.message || 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm(INITIAL_STATE);
    setLocation(null);
    setPhoneError(null);
    setMinutesError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>{isEdit ? 'Edit Mobile Service Appointment' : 'New Mobile Service Appointment'}</DialogTitle>
      <DialogContent>
        <div className={cls.form}>
          <div className={cls.flex}>
            <div className={cls.flexItem}>
              <label className={cls.label} htmlFor='consumer-name'>
                Consumer Name <span className={cls.labelRequired}>*</span>
              </label>
              <TextField
                fullWidth
                name='consumer-name'
                placeholder='Enter consumer name...'
                value={form.consumerName}
                onChange={handleChange('consumerName')}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start' sx={{ marginLeft: 0, marginRight: '6px' }}>
                        <BiSolidUser size={20} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>

            <div className={cls.flexItem}>
              <label className={cls.label} htmlFor='consumer-phone'>
                Consumer Phone
              </label>
              <TextField
                fullWidth
                name='consumer-phone'
                placeholder='Enter phone number...'
                value={form.consumerPhone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={Boolean(phoneError)}
                helperText={phoneError}
                slotProps={{
                  htmlInput: { maxLength: 14 },
                  input: {
                    startAdornment: (
                      <InputAdornment position='start' sx={{ marginLeft: 0, marginRight: '6px' }}>
                        <BiPhone size={20} />
                      </InputAdornment>
                    ),
                    endAdornment: form.consumerPhone.trim() ? (
                      <InputAdornment position='end'>
                        {phoneVerifying ? (
                          <Tooltip title='Validating...'>
                            <span>
                              <BiLoaderCircle
                                className={cls.phoneVerifyLoader}
                                size={20}
                                style={{ color: theme?.palette?.text?.disabled }}
                              />
                            </span>
                          </Tooltip>
                        ) : isMobilePhone === true ? (
                          <Tooltip title='Phone is able to receive SMS.'>
                            <span>
                              <BiSolidCheckCircle size={20} style={{ color: theme?.palette?.success?.main }} />
                            </span>
                          </Tooltip>
                        ) : isMobilePhone === false ? (
                          <Tooltip title='Phone is NOT able to receive SMS!'>
                            <span>
                              <BiErrorCircle size={20} style={{ color: theme?.palette?.error?.main }} />
                            </span>
                          </Tooltip>
                        ) : (
                          <Tooltip title='Please enter a valid mobile phone number with SMS capability. Do NOT include the country code.'>
                            <span>
                              <BiMinusCircle size={20} style={{ color: theme?.palette?.text?.disabled }} />
                            </span>
                          </Tooltip>
                        )}
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
            </div>
          </div>

          <div className={cls.spacer} />

          <MovePlannerLocationSelect
            rooftopId={customerId}
            location={location}
            locations={savedLocations}
            onLocationChange={(loc) => setLocation(loc || null)}
            onLocationFavorite={() => {
              refetchLocations();
            }}
            label='Service Address'
            placeholder='Search saved locations or an address...'
            helperText='Favorites appear first — or search for a new address'
            required
          />

          <div className={cls.spacer} />

          <div className={cls.flex}>
            <div className={cls.flexItem}>
              <label className={cls.label} htmlFor='appointment-time'>
                Appointment Time
              </label>
              <TextField
                fullWidth
                type='datetime-local'
                name='appointment-time'
                value={form.appointmentTime}
                onChange={handleChange('appointmentTime')}
              />
            </div>

            <div className={cls.flexItem}>
              <label className={cls.label} htmlFor='ro-number'>
                RO Number
              </label>
              <TextField
                fullWidth
                name='ro-number'
                placeholder='Enter RO number...'
                value={form.roNumber}
                onChange={handleChange('roNumber')}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start' sx={{ marginLeft: 0, marginRight: '6px' }}>
                        <BiReceipt size={20} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
          </div>

          <div className={cls.spacer} />

          <div className={cls.flex}>
            <div className={cls.flexItem}>
              <label className={cls.label} htmlFor='service-minutes'>
                Est. Service Minutes <span className={cls.labelRequired}>*</span>
              </label>
              <TextField
                fullWidth
                type='number'
                name='service-minutes'
                placeholder={`e.g. ${SERVICE_MINUTES_DEFAULT}`}
                value={form.serviceMinutes}
                onChange={handleChange('serviceMinutes')}
                onBlur={() => checkMinutes(form.serviceMinutes)}
                error={Boolean(minutesError)}
                helperText={minutesError}
                slotProps={{
                  htmlInput: { min: SERVICE_MINUTES_MIN, max: SERVICE_MINUTES_MAX, step: 5 },
                  input: {
                    startAdornment: (
                      <InputAdornment position='start' sx={{ marginLeft: 0, marginRight: '6px' }}>
                        <BiTimeFive size={20} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>

            <div className={cls.flexItem} />
          </div>

          <div className={cls.spacer} />

          <VinLookup
            rooftopId={customerId}
            label='VIN'
            name='appointment-vin'
            placeholder='Enter VIN...'
            value={form.vehicleVin}
            onChange={handleVinChange}
            onInputChange={handleVinInputChange}
            onBlur={() => checkVin(form.vehicleVin)}
            error={Boolean(vinError)}
            helperText={vinError ?? ''}
            queryCall='never'
          />

          <div className={cls.spacer} />

          <div className={cls.flex}>
            <div className={cls.flexItem}>
              <YearSelect
                name='appointment-year'
                label='Year'
                value={form.vehicleYear || null}
                onChange={year => setForm(prev => ({ ...prev, vehicleYear: year || '' }))}
              />
            </div>

            <div className={cls.flexItem}>
              <MakeSelect
                name='appointment-make'
                label='Make'
                value={form.vehicleMake || null}
                onChange={make => setForm(prev => ({ ...prev, vehicleMake: make || '' }))}
                onInputChange={make => setForm(prev => ({ ...prev, vehicleMake: make || '' }))}
              />
            </div>
          </div>

          <div className={cls.spacer} />

          <div className={cls.flex}>
            <div className={cls.flexItem}>
              <ModelSelect
                make={form.vehicleMake || null}
                name='appointment-model'
                label='Model'
                value={form.vehicleModel || null}
                onChange={model => setForm(prev => ({ ...prev, vehicleModel: model || '' }))}
                onInputChange={model => setForm(prev => ({ ...prev, vehicleModel: model || '' }))}
              />
            </div>

            <div className={cls.flexItem}>
              <ColorSelect
                name='appointment-color'
                label='Color'
                value={form.vehicleColor || null}
                onChange={color => setForm(prev => ({ ...prev, vehicleColor: color || '' }))}
              />
            </div>
          </div>

          <div className={cls.spacer} />

          <label className={cls.label} htmlFor='service-notes'>
            Service Notes
          </label>
          <TextField
            fullWidth
            name='service-notes'
            placeholder='Enter service notes...'
            value={form.driverNotes}
            onChange={handleChange('driverNotes')}
            multiline
            rows={3}
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          {loading ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save Changes' : 'Create Appointment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const cls = {
  form: css`
    padding-top: 8px;
  `,
  phoneVerifyLoader: css`
    display: block;
    animation: ${spin} 1s linear infinite;
  `,
  flex: css`
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 12px;
  `,
  flexItem: css`
    flex: 1;
    width: 100%;
  `,
  spacer: css`
    width: 100%;
    height: 12px;
  `,
  label: css`
    display: block;
    margin-bottom: 2px;
    margin-left: 6px;
    font-size: 12px;
    font-weight: 600;
  `,
  labelRequired: css`
    color: #d32f2f;
  `,
};

export default AddAppointmentDialog;
